/**
 * Sheet (Drawer) Atom
 * @layer atom
 * @package @plerity/ui-core
 *
 * Edge-anchored modal panel built on react-aria-components
 * (ModalOverlay + Modal + Dialog). RAC supplies the focus trap, focus restore
 * to the trigger, body scroll lock, `aria-hidden` on the rest of the page,
 * Escape / scrim dismissal, and keeps the node mounted while the exit
 * animation (`data-exiting`) plays.
 */

import { useId } from "react";
import { Button, Dialog, Heading, Modal, ModalOverlay } from "react-aria-components";
import { cn } from "../utils/cn";
import type { SheetProps } from "./Sheet.types";
import {
  sheetBody,
  sheetCloseButton,
  sheetDescription,
  sheetFooter,
  sheetHeader,
  sheetOverlay,
  sheetPanel,
  sheetTitle,
} from "./Sheet.variants";

export function Sheet({
  open,
  onOpenChange,
  side = "right",
  size = "md",
  title,
  description,
  footer,
  children,
  isDismissable = true,
  isKeyboardDismissDisabled = false,
  hideCloseButton = false,
  className,
}: SheetProps) {
  const descriptionId = useId();

  return (
    <ModalOverlay
      isOpen={open}
      onOpenChange={onOpenChange}
      isDismissable={isDismissable}
      isKeyboardDismissDisabled={isKeyboardDismissDisabled}
      className={sheetOverlay({ side })}
    >
      <Modal className={cn(sheetPanel({ side, size }), className)} data-side={side} data-size={size}>
        <Dialog
          aria-describedby={description ? descriptionId : undefined}
          className="flex min-h-0 flex-1 flex-col outline-none"
        >
          <div className={sheetHeader}>
            <div className="min-w-0">
              <Heading slot="title" className={sheetTitle}>
                {title}
              </Heading>
              {description && (
                <p id={descriptionId} className={sheetDescription}>
                  {description}
                </p>
              )}
            </div>
            {!hideCloseButton && (
              <Button slot="close" aria-label="Close" className={sheetCloseButton}>
                <svg
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M18 6 6 18M6 6l12 12" />
                </svg>
              </Button>
            )}
          </div>
          <div className={sheetBody}>{children}</div>
          {footer && <div className={sheetFooter}>{footer}</div>}
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
}

Sheet.displayName = "Sheet";
